import { fromJS } from 'immutable';
import roles from 'shared/constants/RoleConstants';
import _ from 'underscore';

let roleTree = fromJS(roles);

/**
 * Check if a user has a role
 *
 * @param user
 * @param rolePath //see shared/constants/Permissions
 * @param scope
 */
function has(user, rolePath, scope) {
  if (!user) return false;

  scope = scope || {};

  let roleName = roleTree.getIn(rolePath),
    entityType = Object.keys(scope)[0],
    entityId = scope[entityType],
    entityRoles = user.get(entityType + 's');

  if (!entityRoles) return false;

  let foundIndx = entityRoles.findIndex((role) => {
    //If the entity ID (i.e. teamId or eventId) is not present, then just check for the role name match
    return (
      (!entityId || role.get(entityType) === entityId) &&
      role.get('role') === roleName
    );
  });

  return foundIndx !== -1;
}

/**
 * Check if a user at at least one of the roles
 *
 * @param user
 * @param roles
 * @returns {boolean}
 */
function hasSome(user, roles) {
  return _.some(roles, (role) => has(user, role.role, role.scope));
}

/**
 * All roles that have team admin
 *
 * @param teamId
 * @returns {*}
 */
function teamAdminRoles(teamId) {
  return [{ role: ['Team', 'Admin'], scope: { team: teamId } }];
}

/**
 * All roles that have admin (or "greater")
 *
 * @param teamId
 * @param eventId
 * @param includeTeamAdmin
 *
 * @returns {*}
 */

function eventAdminRoles(teamId, eventId, includeTeamAdmin) {
  let adminRoles = [
    { role: ['Event', 'Admin'], scope: { event: eventId } },
    { role: ['Event', 'ShowCaller'], scope: { event: eventId } },
  ];

  if (includeTeamAdmin) return adminRoles.concat(teamAdminRoles(teamId));

  return adminRoles;
}

/**
 * All roles that have contributor (or "greater")
 *
 * @param teamId
 * @param eventId
 * @returns {*}
 */
function eventContributorRoles(teamId, eventId) {
  return eventAdminRoles(teamId, eventId).concat({
    role: ['Event', 'Contributor'],
    scope: { event: eventId },
  });
}

/**
 * Get non-admin roles
 *
 * @param eventId
 * @returns {*[]}
 */
function eventNonAdminRoles(eventId) {
  return [
    { role: ['Event', 'Contributor'], scope: { event: eventId } },
    { role: ['Event', 'ReadOnly'], scope: { event: eventId } },
  ];
}

/**
 * Get all event roles
 *
 * @param eventId
 * @returns {*[]}
 */
function anyEventRole(eventId) {
  return [
    { role: ['Event', 'Contributor'], scope: { event: eventId } },
    { role: ['Event', 'ReadOnly'], scope: { event: eventId } },
    { role: ['Event', 'Admin'], scope: { event: eventId } },
    { role: ['Event', 'ShowCaller'], scope: { event: eventId } },
  ];
}

/**
 * Check to see if they have an admin role in team
 *
 * @param user
 * @param teamId
 * @returns {boolean}
 */
function hasTeamAdminRole(user, teamId) {
  return hasSome(user, teamAdminRoles(teamId));
}

/**
 * Check to see if they have any role in the event
 *
 * @param user
 * @param teamId
 * @param eventId
 * @returns {boolean}
 */
function hasAnyEventRole(user, teamId, eventId) {
  return hasSome(user, anyEventRole(eventId));
}

/**
 * Check to see if they are a team admin with no event roles
 *
 * @param user
 * @param teamId
 * @param eventId
 * @returns {boolean}
 */
function hasTeamAdminWithoutEventRole(user, teamId, eventId) {
  if (!user || !teamId || !eventId) return false;
  return (
    hasTeamAdminRole(user, teamId) && !hasAnyEventRole(user, teamId, eventId)
  );
}

/**
 * Check to see if they have an admin role in an event
 *
 * @param user
 * @param teamId
 * @param eventId
 * @returns {boolean}
 */
function hasEventAdminRole(user, teamId, eventId) {
  if (hasTeamAdminWithoutEventRole(user, teamId, eventId)) return true;
  return hasSome(user, eventAdminRoles(teamId, eventId));
}

/**
 * Check to see if they have a contributor role in the event
 *
 * @param user
 * @param teamId
 * @param eventId
 * @returns {boolean}
 */
function hasEventContributorRole(user, teamId, eventId) {
  if (hasTeamAdminWithoutEventRole(user, teamId, eventId)) return true;
  return hasSome(user, eventContributorRoles(teamId, eventId));
}

/**
 * Check if they are a show caller
 *
 * @param user
 * @param eventId
 * @returns {*}
 */
function hasEventShowCallerRole(user, eventId) {
  return has(user, ['Event', 'ShowCaller'], { event: eventId });
}

export default {
  has,
  hasSome,
  eventAdminRoles,
  teamAdminRoles,
  eventNonAdminRoles,
  hasEventAdminRole,
  hasEventShowCallerRole,
  hasEventContributorRole,
  hasTeamAdminRole,
  hasAnyEventRole,
  hasTeamAdminWithoutEventRole,
};
